import { useEffect, useRef, useState } from "react";

const SPEECH_LANG = { et: "et-EE", ru: "ru-RU", en: "en-US" };

/**
 * Custom hook that wraps the browser's SpeechRecognition API for AI Assistant
 * voice input. Returns a small state machine:
 *
 *   const { supported, listening, start, stop } = useVoiceInput({ lang, onTranscript });
 *
 * `onTranscript(text)` is invoked with the CURRENT concatenated transcript
 * (both interim and final results); the caller decides how to merge it into
 * the input field.
 *
 * Automatically cleans up any in-flight recognition instance on unmount so
 * it never leaks between AIAssistant remounts (e.g. when the user switches
 * languages).
 */
export default function useVoiceInput({ lang, onTranscript }) {
  const [supported, setSupported] = useState(false);
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    setSupported(!!SR);
    return () => {
      if (recRef.current) {
        try { recRef.current.stop(); } catch { /* already stopped */ }
        recRef.current = null;
      }
    };
  }, []);

  const start = (baseText = "") => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const rec = new SR();
    rec.lang = SPEECH_LANG[lang] || "et-EE";
    rec.interimResults = true;
    rec.continuous = false;
    const base = baseText ? baseText + " " : "";
    rec.onresult = (e) => {
      let finalText = "";
      let interim = "";
      for (let i = 0; i < e.results.length; i++) {
        const res = e.results[i];
        if (res.isFinal) finalText += res[0].transcript;
        else interim += res[0].transcript;
      }
      onTranscript?.(base + finalText + interim);
    };
    rec.onstart = () => setListening(true);
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recRef.current = rec;
    rec.start();
  };

  const stop = () => {
    if (recRef.current) {
      try { recRef.current.stop(); } catch { /* noop */ }
    }
  };

  return { supported, listening, start, stop };
}
